import { create } from 'zustand'
import { getEnv } from '@/lib/utils'
import { gatewayFetch } from '@/lib/gateway-auth'

export interface MemoryEntry {
  id: string
  content: string
  type: 'fact' | 'preference' | 'event' | 'insight' | 'task' | 'relationship'
  importance: number
  createdAt: string
  source?: string
}

export interface Skill {
  name: string
  description: string
}

interface MemoryState {
  memoryContent: string
  entries: MemoryEntry[]
  logContent: string
  logDate: string
  skills: Skill[]
  isLoading: boolean
  error: string | null

  fetchMemory: () => Promise<void>
  fetchEntries: (query?: string, limit?: number) => Promise<void>
  fetchLog: (date?: string) => Promise<void>
  fetchSkills: () => Promise<void>
}

function baseUrl(): string {
  const gw = getEnv('VITE_GATEWAY_URL').replace(/\/$/, '')
  return gw || '/api'
}

export const useMemoryStore = create<MemoryState>((set) => ({
  memoryContent: '',
  entries: [],
  logContent: '',
  logDate: '',
  skills: [],
  isLoading: false,
  error: null,

  fetchMemory: async () => {
    set({ isLoading: true, error: null })
    try {
      const res = await gatewayFetch(`${baseUrl()}/coding/memory`)
      if (!res.ok) throw new Error(`Failed to fetch memory: ${res.status}`)
      const data = await res.json()
      set({ memoryContent: data.content || data || '', isLoading: false })
    } catch (e) {
      set({ error: (e as Error).message, isLoading: false })
    }
  },

  fetchEntries: async (query?: string, limit = 50) => {
    set({ isLoading: true, error: null })
    try {
      const params = new URLSearchParams()
      if (query) params.set('query', query)
      params.set('limit', String(limit))
      const res = await gatewayFetch(`${baseUrl()}/coding/memory/entries?${params}`)
      if (!res.ok) throw new Error(`Failed to fetch entries: ${res.status}`)
      const data = await res.json()
      set({ entries: data.entries || data.results || data || [], isLoading: false })
    } catch (e) {
      set({ error: (e as Error).message, isLoading: false })
    }
  },

  fetchLog: async (date?: string) => {
    set({ isLoading: true, error: null })
    try {
      const params = date ? `?date=${date}` : ''
      const res = await gatewayFetch(`${baseUrl()}/coding/memory/log${params}`)
      if (!res.ok) throw new Error(`Failed to fetch log: ${res.status}`)
      const data = await res.json()
      set({ logContent: data.content || data || '', logDate: date || new Date().toISOString().split('T')[0], isLoading: false })
    } catch (e) {
      set({ error: (e as Error).message, isLoading: false })
    }
  },

  fetchSkills: async () => {
    try {
      const res = await gatewayFetch(`${baseUrl()}/coding/skills`)
      if (!res.ok) return
      const data = await res.json()
      set({ skills: data.skills || data || [] })
    } catch {
      // skills are optional
    }
  },
}))
